import { computed, type ComputedRef } from 'vue';
import { useNavigationItemsStore } from '@/stores/navigation-items';
import { useI18n } from 'vue-i18n';
import { useMarketQuery } from '@/composables/market';
import { PermissionName, ProvisionType } from '@containex/portal-backend-dto';
import { Navigation } from '@/router/navigation';
import { NavArea } from '@/types/NavArea';
import { marketContainsProvision } from '@/util/marketContainsProvision';
import { useAuthenticationAction, useAuthenticationQuery } from './authentication';
import { useCompanyQuery } from './company';
import { useLeadQuery } from '@/account/composables/leads';
import type { TypedMenuItem } from '@/types/TypedMenuItem';
import { FEATURES } from '@/util/features';
import type { MenuItem } from 'primevue/menuitem';

export interface NavigationItemsQuery {
    activeArea: ComputedRef<NavArea>;
    navAreas: ComputedRef<Record<NavArea, TypedMenuItem>>;
}

export interface NavigationItemsAction {
    setActiveArea(newValue: NavArea): void;
}

export function useNavigationItemsQuery(): NavigationItemsQuery {
    const store = useNavigationItemsStore();
    const { isUserLoggedIn } = useAuthenticationQuery();
    const authenticationAction = useAuthenticationAction();
    const { isCustomer, isRetailer, isSupplier } = useCompanyQuery();

    const { t } = useI18n();
    const { market } = useMarketQuery();

    return {
        activeArea: computed(() => store.activeArea),

        navAreas: computed(() => {
            let salesNavigationItems: MenuItem[] = [];
            salesNavigationItems = buildSalesNavBarItems(
                authenticationAction.loggedOutOrHasPermission(PermissionName.SALESCATALOG_VIEW)
            );
            const { amountNewLeads } = useLeadQuery();

            const navMenuList: Record<number, TypedMenuItem> = {};

            if (market.value == null) {
                return navMenuList;
            }

            navMenuList[NavArea.LANDING] = {
                name: '',
                hidden: true,
                plainName: 'landing',
                icon: '',
                description: 'landing',
                navArea: NavArea.LANDING,
                rootRouteName: Navigation.Landing,
                provisionType: null,
                navBarItems: [],
            };

            if (marketContainsProvision(market.value, ProvisionType.Sales)) {
                navMenuList[NavArea.BUY] = {
                    name: t('NAVIGATION.AREAS.BUY'),
                    plainName: 'sales',
                    icon: 'pi pi-shopping-cart',
                    description: t('NAVIGATION.AREAS.BUY_DESCRIPTION'),
                    navArea: NavArea.BUY,
                    rootRouteName: Navigation.SalesProductList,
                    provisionType: ProvisionType.Sales,
                    navBarItems: [
                        {
                            label: t('NAVIGATION.MENU_ITEM.PRODUCTS'),
                            route: Navigation.SalesProductList,
                            items: salesNavigationItems,
                        },
                    ],
                };
            }

            if (marketContainsProvision(market.value, ProvisionType.Rental)) {
                navMenuList[NavArea.RENT] = {
                    name: t('NAVIGATION.AREAS.RENT'),
                    plainName: 'rental',
                    icon: 'pi pi-calendar',
                    description: t('NAVIGATION.AREAS.RENT_DESCRIPTION'),
                    navArea: NavArea.RENT,
                    rootRouteName: Navigation.RentalProductList,
                    provisionType: ProvisionType.Rental,
                    navBarItems: [
                        {
                            label: t('NAVIGATION.MENU_ITEM.ALL_PRODUCTS'),
                            route: Navigation.RentalProductList,
                            items: [
                                {
                                    name: ProvisionType.Rental,
                                    label: t('NAVIGATION.MENU_ITEM.ALL_PRODUCTS'),
                                    route: Navigation.RentalProductList,
                                },
                                {
                                    name: ProvisionType.Rental,
                                    label: t('NAVIGATION.MENU_ITEM.SANITARY_CONTAINERS'),
                                    route: Navigation.RentalProductListSanitary,
                                },
                                {
                                    name: ProvisionType.Rental,
                                    label: t('NAVIGATION.MENU_ITEM.STORAGE_CONTAINERS'),
                                    route: Navigation.RentalProductListStorage,
                                },
                                {
                                    name: ProvisionType.Rental,
                                    label: t('NAVIGATION.MENU_ITEM.OFFICE_CONTAINERS'),
                                    route: Navigation.RentalProductListOffice,
                                },
                            ],
                        },
                    ],
                };
            }

            navMenuList[NavArea.MY_AREA] = {
                name: t('NAVIGATION.AREAS.MY_AREA'),
                plainName: 'my-area',
                icon: 'pi pi-user',
                description: t('NAVIGATION.AREAS.MY_AREA_DESCRIPTION'),
                navArea: NavArea.MY_AREA,
                rootRouteName: Navigation.Account,
                provisionType: null,
                navBarItems: isUserLoggedIn.value
                    ? [
                          {
                              label: t('NAVIGATION.MENU_ITEM.OVERVIEW'),
                              route: Navigation.Account,
                          },
                          !(authenticationAction.hasPermission(PermissionName.ORDERS_VIEW) && isCustomer.value)
                              ? undefined
                              : {
                                    name: ProvisionType.Sales,
                                    label: t('NAVIGATION.MENU_ITEM.ORDERS'),
                                    route: Navigation.OrderList,
                                },
                          !(
                              authenticationAction.hasPermission(PermissionName.LEADS_VIEW) &&
                              isRetailer.value &&
                              isCustomer.value
                          )
                              ? undefined
                              : {
                                    label: t('NAVIGATION.MENU_ITEM.LEADS'),
                                    route: Navigation.Leads,
                                    count: amountNewLeads.value,
                                },
                          isSupplier.value && FEATURES.SUPPLIER_PORTAL
                              ? {
                                    label: t('ACCOUNT.OVERVIEW_VIEW.DEPOT_STOCK'),
                                    route: Navigation.DepotStock,
                                }
                              : undefined,
                      ].filter((item) => item != null)
                    : [],
            };

            return navMenuList;
        }),
    };

    function buildSalesNavBarItems(hasViewSalesCataloguePermission: boolean): MenuItem[] {
        const baseItems: MenuItem[] = [
            {
                name: ProvisionType.Sales,
                label: t('NAVIGATION.MENU_ITEM.ALL_PRODUCTS'),
                route: Navigation.SalesProductList,
            },
            {
                name: ProvisionType.Sales,
                label: t('NAVIGATION.MENU_ITEM.NEW_PRODUCTS'),
                route: Navigation.SalesProductListNew,
            },
            {
                name: ProvisionType.Sales,
                label: t('NAVIGATION.MENU_ITEM.USED_PRODUCTS'),
                route: Navigation.SalesProductListUsed,
            },
        ];
        if (hasViewSalesCataloguePermission) {
            baseItems.push({
                name: ProvisionType.Sales,
                label: t('NAVIGATION.MENU_ITEM.SALE'),
                route: Navigation.SalesProductListSale,
                class: 'sales-menu-item',
            });
        }
        return baseItems;
    }
}

export function useNavigationItemsAction(): NavigationItemsAction {
    const store = useNavigationItemsStore();

    return {
        setActiveArea(newValue: NavArea): void {
            store.setActiveArea(newValue);
        },
    };
}
